import { FetchArgs } from "@reduxjs/toolkit/query";
import api from "../api";
import {
  GetFileSystemRequest,
  FileSystemResponse,
  DownloadFileRequest,
  DownloadFileResponse,
  DownloadArchiveRequest,
  DownloadArchiveResponse,
  BrowserFolderCreateRequest,
  BrowserFolderCreateResponse,
  BrowserFolderRenameRequest,
  BrowserFolderRenameResponse,
  BrowserUploadPresignRequest,
  BrowserUploadPresignResponse,
  BrowserUploadFinalizeRequest,
  BrowserUploadFinalizeResponse,
  BrowserDeleteFileRequest,
  BrowserDeleteFileResponse,
  BrowserDeleteFolderRequest,
  BrowserDeleteFolderResponse,
  BrowserMoveFileRequest,
  BrowserMoveFileResponse,
  BrowserRenameFileRequest,
  BrowserRenameFileResponse,
  ConnectFolderOptionsRequest,
  ConnectFolderOptionsResponse,
  ConnectFolderStorageCreateRequest,
  ConnectFolderStorageCreateResponse,
  ConnectFolderStorageDeleteRequest,
  ConnectFolderStorageDeleteResponse,
  ConnectFolderIntegrationsResponse,
  ConnectFolderCreateRequest,
  ConnectFolderCreateResponse,
  FolderSyncStatusResponse,
  DisconnectUserFolderRequest,
  DisconnectUserFolderResponse,
  FolderDisconnectStatusRequest,
  FolderDisconnectStatusResponse,
  UpdateUserFolderDisplayNameRequest,
  UpdateUserFolderDisplayNameResponse,
  StartTopFolderZipResponse,
  StartTopFolderZipBatchResponse,
  TopFolderZipReadyBadgesResponse,
} from "../../types/interfaces";

export type TopFolderZipStatusBatchResultItem =
  | {
      job_id: string;
      status: string;
      error_message: string | null;
    }
  | { job_id: string; error: string };

export type TopFolderZipStatusBatchResponse = {
  results: TopFolderZipStatusBatchResultItem[];
};

const getFileSystemTagId = (prefix: string) => prefix || "__root__";

const getParentPrefix = (prefix: string): string | null => {
  const parts = (prefix || "").trim().split("/").filter(Boolean);
  if (parts.length < 2) return null;
  parts.pop();
  return parts.join("/") || null;
};

export const filesStorageApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getFileSystem: builder.query<FileSystemResponse, GetFileSystemRequest>({
      query: ({
        prefix,
        continuation_token,
        search,
        limit,
        folder_type,
        page,
        sort_field,
        sort_order,
      }) => {
        return {
          url: `/web/folders/`,
          params: {
            prefix: prefix,
            search: search || undefined,
            continuation_token: continuation_token || undefined,
            limit: limit ?? 100,
            folder_type:
              folder_type && folder_type !== "all" ? folder_type : undefined,
            page: page ?? 1,
            sort_field: sort_field ?? "date",
            sort_order: sort_order ?? "desc",
          },
        };
      },
      serializeQueryArgs: ({ queryArgs }) => {
        const {
          prefix = "",
          continuation_token = null,
          search = null,
          limit = 100,
          folder_type = "all",
          page = 1,
          sort_field = "date",
          sort_order = "desc",
        } = queryArgs;
        return JSON.stringify({
          prefix,
          continuation_token,
          search,
          limit,
          folder_type,
          page,
          sort_field,
          sort_order,
        });
      },
      providesTags: (_result, _error, arg) => [
        { type: "FileSystem", id: getFileSystemTagId(arg.prefix) },
      ],
    }),

    createBrowserFolder: builder.mutation<
      BrowserFolderCreateResponse,
      BrowserFolderCreateRequest
    >({
      query: ({ prefix, folder_name }) => ({
        url: `/web/files/folders/create-browser/`,
        method: "POST",
        body: {
          prefix,
          folder_name,
        },
      }),
      invalidatesTags: (_result, _error, arg) => {
        const tags = [
          { type: "FileSystem" as const, id: getFileSystemTagId(arg.prefix) },
        ];
        const parent = getParentPrefix(arg.prefix);
        if (parent != null) {
          tags.push({ type: "FileSystem", id: getFileSystemTagId(parent) });
        }
        return tags;
      },
    }),

    presignBrowserUpload: builder.mutation<
      BrowserUploadPresignResponse,
      BrowserUploadPresignRequest
    >({
      query: ({ prefix, file_name, content_type }) => ({
        url: `/web/files/upload/presign/`,
        method: "POST",
        body: {
          prefix,
          file_name,
          content_type,
        },
      }),
    }),

    finalizeBrowserUpload: builder.mutation<
      BrowserUploadFinalizeResponse,
      BrowserUploadFinalizeRequest
    >({
      query: ({ prefix, file_name, file_size, content_type }) => ({
        url: `/web/files/upload/finalize/`,
        method: "POST",
        body: {
          prefix,
          file_name,
          file_size,
          content_type,
        },
      }),
      invalidatesTags: (_result, _error, arg) => {
        const tags = [
          { type: "FileSystem" as const, id: getFileSystemTagId(arg.prefix) },
        ];
        const parent = getParentPrefix(arg.prefix);
        if (parent != null) {
          tags.push({ type: "FileSystem", id: getFileSystemTagId(parent) });
        }
        return tags;
      },
    }),

    deleteBrowserFile: builder.mutation<
      BrowserDeleteFileResponse,
      BrowserDeleteFileRequest
    >({
      query: ({ prefix, source }) => ({
        url: `/web/files/delete/`,
        method: "DELETE",
        body: {
          prefix,
          source,
        },
      }),
      invalidatesTags: (_result, _error, arg) => {
        const tags = [
          { type: "FileSystem" as const, id: getFileSystemTagId(arg.prefix) },
        ];
        const parent = getParentPrefix(arg.prefix);
        if (parent != null) {
          tags.push({ type: "FileSystem", id: getFileSystemTagId(parent) });
        }
        return tags;
      },
    }),

    renameBrowserFile: builder.mutation<
      BrowserRenameFileResponse,
      BrowserRenameFileRequest
    >({
      query: ({ source, new_name }) => ({
        url: `/web/files/rename/`,
        method: "PATCH",
        body: {
          source,
          new_name,
        },
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: "FileSystem", id: getFileSystemTagId(arg.prefix) },
      ],
    }),

    moveBrowserFile: builder.mutation<
      BrowserMoveFileResponse,
      BrowserMoveFileRequest
    >({
      query: ({ source, destination_prefix, prefix }) => ({
        url: `/web/files/move/`,
        method: "POST",
        body: {
          source,
          destination_prefix,
          prefix,
        },
      }),
      invalidatesTags: (_result, _error, arg) => {
        const tags = [
          { type: "FileSystem" as const, id: getFileSystemTagId(arg.prefix) },
        ];
        if (arg.destination_prefix) {
          tags.push({
            type: "FileSystem" as const,
            id: getFileSystemTagId(arg.destination_prefix),
          });
        }
        return tags;
      },
    }),

    deleteBrowserFolder: builder.mutation<
      BrowserDeleteFolderResponse,
      BrowserDeleteFolderRequest
    >({
      query: ({ prefix }) => ({
        url: `/web/files/folders/delete-browser/`,
        method: "DELETE",
        body: {
          prefix,
        },
      }),
      invalidatesTags: (_result, _error, arg) => {
        const grandparent = getParentPrefix(arg.parent_prefix);
        if (grandparent != null) {
          return [
            {
              type: "FileSystem" as const,
              id: getFileSystemTagId(grandparent),
            },
          ];
        }
        return [];
      },
    }),

    renameBrowserFolder: builder.mutation<
      BrowserFolderRenameResponse,
      BrowserFolderRenameRequest
    >({
      query: ({ prefix, new_name }) => ({
        url: `/web/files/folders/rename-browser/`,
        method: "PATCH",
        body: {
          prefix,
          new_name,
        },
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: "FileSystem", id: getFileSystemTagId(arg.parent_prefix) },
        // A folder moved in storage no longer lives at its old path.
        { type: "FileSystem", id: getFileSystemTagId(arg.prefix) },
      ],
    }),

    getConnectFolderOptions: builder.query<
      ConnectFolderOptionsResponse,
      ConnectFolderOptionsRequest
    >({
      query: ({ user_id, is_sftp_bucket, prefix }) => ({
        url: `/web/folders/connect/options/`,
        method: "GET",
        params: {
          is_sftp_bucket,
          ...(user_id != null ? { user_id } : {}),
          ...(prefix ? { prefix } : {}),
        },
      }),
    }),

    createConnectFolder: builder.mutation<
      ConnectFolderStorageCreateResponse,
      ConnectFolderStorageCreateRequest
    >({
      query: (body) => ({
        url: `/web/folders/connect/create/`,
        method: "POST",
        body,
      }),
    }),

    deleteConnectFolder: builder.mutation<
      ConnectFolderStorageDeleteResponse,
      ConnectFolderStorageDeleteRequest
    >({
      query: (body) => ({
        url: `/web/folders/connect/delete/`,
        method: "POST",
        body,
      }),
    }),

    getConnectFolderIntegrations: builder.query<
      ConnectFolderIntegrationsResponse,
      void
    >({
      query: () => ({
        url: `/web/folders/connect/integrations/`,
        method: "GET",
      }),
    }),

    connectFolderCreate: builder.mutation<
      ConnectFolderCreateResponse,
      ConnectFolderCreateRequest
    >({
      query: (body) => ({
        url: `/web/folders/create/`,
        method: "POST",
        body,
      }),
      invalidatesTags: () => [
        { type: "FileSystem", id: getFileSystemTagId("") },
      ],
    }),

    disconnectUserFolder: builder.mutation<
      DisconnectUserFolderResponse,
      DisconnectUserFolderRequest
    >({
      query: ({ folder_id }) => ({
        url: `/web/folders/${folder_id}/delete/`,
        method: "DELETE",
      }),
      invalidatesTags: () => [],
    }),

    getFolderDisconnectStatus: builder.query<
      FolderDisconnectStatusResponse,
      FolderDisconnectStatusRequest
    >({
      query: ({ job_id }) => ({
        url: `/web/folders/disconnect/status/`,
        method: "GET",
        params: { job_id },
      }),
    }),

    updateUserFolderDisplayName: builder.mutation<
      UpdateUserFolderDisplayNameResponse,
      UpdateUserFolderDisplayNameRequest
    >({
      query: ({ folder_id, folder }) => ({
        url: `/web/folders/${folder_id}/`,
        method: "PATCH",
        body: { folder },
      }),
      invalidatesTags: () => [
        { type: "FileSystem", id: getFileSystemTagId("") },
      ],
    }),

    getFolderSyncStatus: builder.query<FolderSyncStatusResponse, number>({
      query: (folder_id) => ({
        url: `/web/folders/sync-status/`,
        method: "GET",
        params: { folder_id },
      }),
    }),

    downloadFile: builder.query<DownloadFileResponse, DownloadFileRequest>({
      query: ({ source }): FetchArgs => ({
        url: `/web/files/download_url/`,
        method: "GET",
        params: {
          fname: source,
        },
        responseHandler: (response: Response) => response.json(),
      }),
    }),

    downloadArchive: builder.query<
      DownloadArchiveResponse,
      DownloadArchiveRequest & { next_token?: string | null }
    >({
      query: ({ source, next_token }): FetchArgs => ({
        url: `/web/files/download/`,
        method: "GET",
        params: {
          source,
          ...(next_token ? { next_token } : {}),
        },
        responseHandler: (response: Response) => response.json(),
      }),
    }),

    startTopFolderZip: builder.mutation<
      StartTopFolderZipResponse,
      { source: string }
    >({
      query: (body) => ({
        url: `/web/files/top-folder-zip/start/`,
        method: "POST",
        body,
      }),
    }),

    startTopFolderZipBatch: builder.mutation<
      StartTopFolderZipBatchResponse,
      { sources: string[] }
    >({
      query: (body) => ({
        url: `/web/files/top-folder-zip/start-batch/`,
        method: "POST",
        body,
      }),
    }),

    topFolderZipReadyBadges: builder.mutation<
      TopFolderZipReadyBadgesResponse,
      { sources: string[]; verify_fingerprint?: boolean }
    >({
      query: (body) => ({
        url: `/web/files/top-folder-zip/ready-badges/`,
        method: "POST",
        body,
      }),
    }),

    getTopFolderZipStatus: builder.query<
      { job_id: string; status: string; error_message: string | null },
      string
    >({
      query: (jobId) => ({
        url: `/web/files/top-folder-zip/${jobId}/status/`,
        method: "GET",
      }),
    }),

    topFolderZipStatusBatch: builder.mutation<
      TopFolderZipStatusBatchResponse,
      { job_ids: string[] }
    >({
      query: (body) => ({
        url: `/web/files/top-folder-zip/status/`,
        method: "POST",
        body,
      }),
    }),

    getTopFolderZipDownload: builder.query<
      { download_url: string; filename: string },
      string
    >({
      query: (jobId) => ({
        url: `/web/files/top-folder-zip/${jobId}/download/`,
        method: "GET",
      }),
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetFileSystemQuery,
  useLazyGetFileSystemQuery,
  useCreateBrowserFolderMutation,
  usePresignBrowserUploadMutation,
  useFinalizeBrowserUploadMutation,
  useDeleteBrowserFileMutation,
  useRenameBrowserFileMutation,
  useMoveBrowserFileMutation,
  useDeleteBrowserFolderMutation,
  useRenameBrowserFolderMutation,
  useLazyGetConnectFolderOptionsQuery,
  useCreateConnectFolderMutation,
  useDeleteConnectFolderMutation,
  useGetConnectFolderIntegrationsQuery,
  useConnectFolderCreateMutation,
  useDisconnectUserFolderMutation,
  useUpdateUserFolderDisplayNameMutation,
  useGetFolderDisconnectStatusQuery,
  useLazyGetFolderSyncStatusQuery,
  useLazyDownloadFileQuery,
  useLazyDownloadArchiveQuery,
  useStartTopFolderZipMutation,
  useStartTopFolderZipBatchMutation,
  useTopFolderZipReadyBadgesMutation,
  useLazyGetTopFolderZipStatusQuery,
  useLazyGetTopFolderZipDownloadQuery,
  useTopFolderZipStatusBatchMutation,
} = filesStorageApi;
